import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, ChevronRight, UserPlus } from 'lucide-react';
import { StatusPill } from '@/components/StatusPill';
import { Button } from '@/components/Button';
import { useAuth } from '@/hooks/useAuth';
import { settingsService, PlanUsage } from '@/services/settings.service';
import { SETTINGS_SECTIONS } from '@/components/settings/SettingsShell';

const initialsOf = (name: string) => name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

/** Settings landing: who you are, where you stand, and every settings page grouped with a one-line purpose. */
export default function SettingsHub() {
  const { user, profile } = useAuth();
  const [plan, setPlan] = useState<PlanUsage | null>(null);
  useEffect(() => { settingsService.plan().then(setPlan).catch(() => undefined); }, []);

  const company = plan?.company;
  const daysLeft = company ? Math.round((new Date(company.expiresAt).getTime() - Date.now()) / 86_400_000) : null;
  const isAdmin = user?.role === 'company_admin';

  return (
    <div className="@container space-y-6">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-surface p-6 shadow-soft @xl:p-7">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" aria-hidden />
        <div className="relative flex flex-col gap-5 @2xl:flex-row @2xl:items-center">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gold-gradient-soft font-display text-xl font-bold text-primary shadow-soft">
            {company ? initialsOf(company.name) : '…'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-2xs font-semibold uppercase tracking-[0.16em] text-primary">Settings</div>
            <h1 className="mt-0.5 truncate font-display text-2xl font-bold tracking-tight text-text-primary">{company?.name ?? 'Your workspace'}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-muted">
              {company && <StatusPill status={company.status} />}
              {plan?.plan && <span className="rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">{plan.plan.displayName} plan</span>}
              {daysLeft !== null && (
                <span className="inline-flex items-center gap-1"><CalendarClock size={12} />{daysLeft < 0 ? `Expired ${-daysLeft}d ago` : `Renews in ${daysLeft}d`}</span>
              )}
              {profile && <span>Signed in as {profile.firstName} {profile.lastName} · {isAdmin ? 'Admin' : 'User'}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            {isAdmin && <Link to="/settings/team"><Button variant="secondary" size="sm" iconLeft={<UserPlus size={14} />}>Invite teammate</Button></Link>}
            <Link to="/settings/plan"><Button size="sm" iconRight={<ArrowRight size={14} />}>Plan & usage</Button></Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
        {SETTINGS_SECTIONS.map((s) => (
          <section key={s.title} className="flex min-w-0 flex-col rounded-2xl border border-border bg-surface shadow-soft">
            <div className="flex items-start gap-3 px-5 pt-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><s.icon size={18} /></span>
              <div className="min-w-0">
                <h2 className="font-display text-base font-semibold tracking-tight text-text-primary">{s.title}</h2>
                <p className="mt-0.5 text-xs text-text-muted">{s.blurb}</p>
              </div>
            </div>
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {s.tabs.map((t) => (
                <li key={t.to}>
                  <Link to={t.to} className="group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-alt">
                    <t.icon size={15} className="shrink-0 text-text-muted transition-colors group-hover:text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-text-primary">{t.label}</div>
                      <div className="truncate text-2xs text-text-muted">{t.description}</div>
                    </div>
                    <ChevronRight size={14} className="shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
